import type { StorageBinRow } from '@/hooks/inventory';

/** The server's rule for a bin code: letters and digits, with . _ / - and spaces between. */
export const BIN_CODE = /^[A-Za-z0-9][A-Za-z0-9._/ -]{0,23}$/;
/** Bins added in one go; the server refuses more. */
export const MAX_BINS_AT_ONCE = 500;
export type NewBin = { code: string; nameEn: string };

/**
 * Spells out a range: prefix "A-", from "001", to "100" gives A-001 … A-100.
 * Zero-padding follows what was typed in From, so "1" gives A-1 and "001"
 * gives A-001. Names are the name prefix and the plain number ("Rack 7"), or
 * the code when there is no name prefix.
 */
export function expandRange(input: {
  prefix: string;
  from: string;
  to: string;
  namePrefix: string;
}): { bins: NewBin[]; problem: string | null } {
  const from = input.from.trim();
  const to = input.to.trim();
  if (!from || !to) return { bins: [], problem: null };
  if (!/^\d+$/.test(from) || !/^\d+$/.test(to)) {
    return { bins: [], problem: 'From and To are whole numbers, such as 1 and 100.' };
  }
  const start = Number(from);
  const end = Number(to);
  if (end < start) return { bins: [], problem: `To must be ${start} or more.` };
  const count = end - start + 1;
  if (count > MAX_BINS_AT_ONCE) {
    return {
      bins: [],
      problem: `That is ${count} bins. Add at most ${MAX_BINS_AT_ONCE} at a time.`,
    };
  }

  const prefix = input.prefix.trim();
  const namePrefix = input.namePrefix.trim();
  const bins: NewBin[] = [];
  for (let n = start; n <= end; n++) {
    const code = prefix + String(n).padStart(from.length, '0');
    bins.push({ code, nameEn: namePrefix ? `${namePrefix} ${n}` : code });
  }
  const bad = bins.find((b) => !BIN_CODE.test(b.code));
  if (bad) {
    return {
      bins: [],
      problem: `"${bad.code}" is not a usable code. Codes start with a letter or digit, can hold . _ / - and spaces, and are up to 24 characters.`,
    };
  }
  return { bins, problem: null };
}

export type Clash = { bin: NewBin; existing: StorageBinRow; by: 'code' | 'name' };

/** Splits a range into bins the store does not have yet, and those it does (by code or by name). */
export function splitClashes(
  bins: NewBin[],
  existing: StorageBinRow[],
): { fresh: NewBin[]; clashing: Clash[] } {
  const byCode = new Map(existing.map((b) => [b.code.toLowerCase(), b]));
  const byName = new Map(existing.map((b) => [b.nameEn.toLowerCase(), b]));
  const fresh: NewBin[] = [];
  const clashing: Clash[] = [];
  for (const bin of bins) {
    const sameCode = byCode.get(bin.code.toLowerCase());
    const sameName = byName.get(bin.nameEn.toLowerCase());
    if (sameCode) clashing.push({ bin, existing: sameCode, by: 'code' });
    else if (sameName) clashing.push({ bin, existing: sameName, by: 'name' });
    else fresh.push(bin);
  }
  return { fresh, clashing };
}
